import React, { useState } from "react";
import { toast } from "sonner";
import { Bot, Mail, Webhook, Plus, Trash2, Zap } from "lucide-react";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const WH_TYPES = ["Server Status", "Events", "Economy", "Announcements", "Audit Alerts"];

export default function Integrations() {
    const { data: info } = useFetch("/admin/integrations");
    const { data: webhooks, reload } = useFetch("/admin/webhooks");
    const [form, setForm] = useState({ type: "Server Status", url: "", enabled: true });
    if (!info || !webhooks) return <Loader />;

    const add = async () => {
        if (!form.url.startsWith("https://")) { toast.error("Enter a valid https webhook URL"); return; }
        await api.post("/admin/webhooks", form); toast.success("Webhook added"); setForm({ ...form, url: "" }); reload();
    };
    const test = async (w) => {
        try { await api.post(`/admin/webhooks/${w.id}/test`); toast.success("Test sent"); }
        catch (e) { toast.error("Test failed", { description: e.response?.data?.detail || "" }); }
    };

    return (
        <div data-testid="admin-integrations">
            <PageHeader title="Integrations" subtitle="External services and outgoing webhooks." />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                <SectionCard testId="integration-discord">
                    <div className="flex items-center gap-2"><Bot size={18} className="text-discord" /><span className="font-heading font-bold uppercase text-foreground">Discord</span></div>
                    <div className="mt-3"><StatusBadge status={info.discord.demo_mode ? "demo" : info.discord.bot_online ? "connected" : "disconnected"} /></div>
                    <p className="text-sm text-muted-foreground mt-2">Guild: {info.discord.guild}</p>
                </SectionCard>
                <SectionCard testId="integration-email">
                    <div className="flex items-center gap-2"><Mail size={18} className="text-hazard" /><span className="font-heading font-bold uppercase text-foreground">Email</span></div>
                    <div className="mt-3"><StatusBadge status={info.email.configured ? "connected" : "offline"} label={info.email.configured ? "CONFIGURED" : "OFF"} /></div>
                    <p className="text-sm text-muted-foreground mt-2">{info.email.provider}</p>
                </SectionCard>
                <SectionCard testId="integration-webhooks">
                    <div className="flex items-center gap-2"><Webhook size={18} className="text-radioactive" /><span className="font-heading font-bold uppercase text-foreground">Webhooks</span></div>
                    <div className="font-heading text-3xl font-bold text-foreground mt-2">{info.webhooks}</div>
                    <p className="text-sm text-muted-foreground">active outgoing hooks</p>
                </SectionCard>
            </div>

            <SectionCard title="Discord Webhooks" testId="webhook-manager">
                <div className="flex flex-col md:flex-row gap-2 mb-4">
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                        <SelectTrigger data-testid="webhook-type" className="md:w-52 bg-background border-border"><SelectValue /></SelectTrigger>
                        <SelectContent>{WH_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input data-testid="webhook-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://discord.com/api/webhooks/..." className="bg-background border-border flex-1" />
                    <Button data-testid="webhook-add" onClick={add} className="font-heading uppercase bg-hazard text-black hover:bg-hazard/90"><Plus size={15} className="mr-1" /> Add</Button>
                </div>
                <div className="space-y-2">
                    {webhooks.length === 0 && <div className="text-sm text-muted-foreground">No webhooks configured.</div>}
                    {webhooks.map((w) => (
                        <div key={w.id} data-testid={`webhook-${w.id}`} className="flex items-center justify-between rounded-md border border-border bg-background/40 p-3">
                            <div className="min-w-0">
                                <div className="font-heading font-semibold uppercase text-foreground">{w.type}</div>
                                <div className="label-mono text-muted-foreground truncate max-w-[300px]">{w.url}</div>
                            </div>
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="border-radioactive/40 text-radioactive hover:bg-radioactive/10" onClick={() => test(w)}><Zap size={13} className="mr-1" /> Test</Button>
                                <Button size="icon" variant="outline" className="border-danger/40 text-danger hover:bg-danger/10 h-8 w-8" onClick={async () => { await api.delete(`/admin/webhooks/${w.id}`); reload(); }}><Trash2 size={13} /></Button>
                            </div>
                        </div>
                    ))}
                </div>
            </SectionCard>
            <Footer />
        </div>
    );
}
